import { useEffect, useMemo } from 'react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { TAnyClientQuestionType } from '@hubertai/screening-question-types';
import { useQuestionEditorContext, ECreateQuestionMode } from '../context/create-question.context';
import { validateNewQuestion } from '../utils';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertTooltip from 'components/HubertUI/HubertTooltip';
import { useTemplateBuilderContext } from 'components/Templates/builder/context/template-builder.context';
import { useUser } from 'hooks/useUser';
import { useCreateQuestionMutation } from 'rtk/query/apis/questions';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { setRefreshPreview } from 'rtk/Question.Editor.Slice';
import { makeSelectFullQuestion, selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { ETemplateRevision } from 'rtk/TemplateSlice';
import { useSwapQuestionMutation } from 'rtk/query/apis/templates';
import { tryGetErrorMessage } from 'utils/tryGetErrorMessage';

interface CopyAndSwapQuestionProps extends Omit<ComponentProps<typeof HubertButton>, 'onClick'> {
  buttonText?: string;
  onClick?: (q: TAnyClientQuestionType) => void;
  onCreateQuestionFinished?: (q: TAnyClientQuestionType) => void;
  originalQuestion: TAnyClientQuestionType | null | undefined;
  copyBranching?: boolean;
  onBusyChange?: (busy: boolean) => void;
}

export const CopyAndSwapQuestion = ({
  buttonText,
  onClick,
  onCreateQuestionFinished,
  originalQuestion,
  copyBranching = true,
  onBusyChange,
  ...props
}: CopyAndSwapQuestionProps) => {
  const { t } = useTranslation('Questionsv2');
  const user = useUser();
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const { templateReferenceId, processId } = useTemplateBuilderContext();
  const selectFullQuestionForId = useMemo(() => makeSelectFullQuestion(), []);

  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const q = useAppSelector((state) => selectFullQuestionForId(state, questionId));
  const isPublishedOrNonExclusiveQuestion = useAppSelector(
    (state) => state.questionEditor.byId[questionId].isPublishedOrNonExclusiveQuestion,
  );

  const [createQuestionRequest, { isLoading }] = useCreateQuestionMutation();
  const { revision } = useTemplateBuilderContext();
  const isInDraft = revision === ETemplateRevision.Draft;
  const showToolTip = mode === ECreateQuestionMode.InTemplateEdit && isPublishedOrNonExclusiveQuestion && isInDraft;
  const [swapQuestionRequest, { isLoading: isSwapLoading }] = useSwapQuestionMutation();

  const isBusy = isLoading || isSwapLoading;

  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  const defaultCreate = async () => {
    const question = validateNewQuestion(q, questionLanguage, user, mode, originalQuestion);

    const sourceQuestionId = originalQuestion?.id ?? '';

    if (!sourceQuestionId || !sourceQuestionId.length) {
      toast.error(t('copyAndSwap.toast.sourceRequired'));
      return;
    }

    if (!question) {
      toast.error(t('copyAndSwap.toast.validationFailed'));
      return;
    }

    try {
      toast.info(t('copyAndSwap.toast.creating'));
      const result = await createQuestionRequest(question).unwrap();

      if (!result.question) {
        toast.error(`${result}`);
        return;
      }

      toast.success(t('copyAndSwap.toast.saved', { id: q.id }));
      dispatch(setRefreshPreview({ questionId }));

      toast.info(t('copyAndSwap.toast.swapping', { sourceId: sourceQuestionId, newId: q.id }));
      await swapQuestionRequest({
        templateReferenceId,
        processId,
        questionId: sourceQuestionId,
        copiedIdMap: q.copiedIdMap ?? {},
        copyBranching,
      }).unwrap();

      toast.success(t('copyAndSwap.toast.swapped', { id: q.id }));

      if (onCreateQuestionFinished) {
        onCreateQuestionFinished(result.question);
      }
    } catch (error) {
      toast.error(t('copyAndSwap.toast.somethingWentWrong', { error: tryGetErrorMessage(error) ?? String(error) }));
    }
  };

  return (
    <HubertTooltip>
      <HubertTooltip.Trigger>
        <HubertButton
          {...props}
          className=""
          loading={isBusy}
          onClick={() => {
            const question = validateNewQuestion(q, questionLanguage, user, mode, originalQuestion);
            if (!question) {
              toast.error(t('copyAndSwap.toast.updateValidationFailed'));
              return;
            }

            if (onClick) {
              onClick(question);
            } else {
              defaultCreate();
            }
          }}
        >
          {buttonText ?? t('copyAndSwap.defaultButton')}
        </HubertButton>
      </HubertTooltip.Trigger>
      {showToolTip && <HubertTooltip.Content>{t('builder.uneditableTooltip')}</HubertTooltip.Content>}
    </HubertTooltip>
  );
};
